import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { Button } from '@innovyze/stylovyze';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useGlobalization } from '@Translations/useGlobalization';
import StarIcon from '@mui/icons-material/Star';
import { StyledGradeMatrixDataGrid, useGradeMatrixStyles } from './GradeMatrixStyles';
import { Box, Divider, Typography } from '@mui/material';
import { legends } from './utils.GradeMatrix';
import { DeteriorationModelFailureGradeRange, FailureGradeCell } from '@Types/failureGrade.types';
import { selectDeteriorationModel, selectFailureGradeMatrixIsLoading } from '@Selectors';
import {
	GradeMatrixCell,
	MatrixCell,
	RangeEditorDialog,
} from '@Components/deteriorationModel/gradeSetupTab/FailureGradeMatrixComponents';

//**** MAIN COMPONENT - DATAGRID for Grade Matrix ****************/

export const fpHeaderCell = () => {
	const { t } = useGlobalization();
	const classes = useGradeMatrixStyles();
	return (
		<div className={classes.fprulheadercell} id="lofheadercell">
			<ArrowDownwardIcon className={classes.downIcon} />
			<div>{t('FP / RUL')}</div>
			<ArrowForwardIcon className={classes.fwdIcon} />
		</div>
	);
};

export const rulHeaderCell = (rangeItem: string) => {
	const classes = useGradeMatrixStyles();
	return (
		<div className={classes.fprulheadercell} id="rulheadercell">
			<div>&le; {rangeItem} years</div>
		</div>
	);
};

export const fpCell = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	props: GridRenderCellParams<any, any>,
) => {
	const classes = useGradeMatrixStyles();
	const { value } = props;
	return (
		<div id="fp-cell" className={classes.fpCell}>
			<div> {value} </div>
		</div>
	);
};

interface FailureGradeMatrixProps {
	failureGradeMatrix?: FailureGradeCell[][];
	ranges?: DeteriorationModelFailureGradeRange;
	showEditRangesDialog: boolean;
	setShowEditRangesDialog: Dispatch<SetStateAction<boolean>>;
}

export const FailureGradeMatrix = (props: FailureGradeMatrixProps) => {
	const { t } = useGlobalization();
	const classes = useGradeMatrixStyles();
	const { failureGradeMatrix, ranges, showEditRangesDialog, setShowEditRangesDialog } = props;
	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;
	const isLoading = selectFailureGradeMatrixIsLoading();

	// 1. Define the type for a single row of data in the DataGrid
	// It will have an `id` and a field for each RUL.
	type GridRow = {
		id: string; // The `id` is required by DataGrid
		failureProbability: number; // The row header (Y-axis)
		modelId?: string; // Model ID for grade updates
		[key: string]: string | number | MatrixCell | undefined; // Dynamic keys for RULs
	};
	const columns: GridColDef[] = useMemo(() => {
		if (!ranges) return [];
		// The first column is the Y-axis header (Failure Probabilities)
		const yAxisColumn: GridColDef = {
			field: 'failureProbability',
			renderHeader: fpHeaderCell,
			width: 120,
			sortable: false,
			align: 'left',
			cellClassName: classes.headerLikeCell,
			renderCell: (params: GridRenderCellParams<GridRow, MatrixCell>) => (
				<div style={{ backgroundColor: '#f7f7f7', padding: 8, fontWeight: 600 }}>
					&le; {params.row.failureProbability} %
				</div>
			),
		};

		// The other columns are the X-axis headers (RULs)
		const rulsColumns: GridColDef[] = ranges.rulRanges.map((rul, index) => ({
			field: `rul-${index}`, // A unique field name for each column
			renderHeader: () => rulHeaderCell(rul.toString()),
			width: 120,
			sortable: false,
			// Use renderCell to display the complex data (grade and count)
			renderCell: GradeMatrixCell,
		}));
		return [yAxisColumn, ...rulsColumns];
	}, [ranges?.rulRanges]);

	const rows: GridRow[] = useMemo(() => {
		if (!failureGradeMatrix || !ranges) return [];
		// Transform the matrix into the row-based format DataGrid expects
		return failureGradeMatrix?.map((row, rowIndex) => {
			const rowData: GridRow = {
				id: `row-${rowIndex}`, // Unique ID for each row
				failureProbability: ranges ? ranges.fpRanges[rowIndex] : 0,
				modelId: currentDeteriorationModel?.id || '',
			};

			// Populate the row with the data from the matrix
			row.forEach((cell, cellIndex) => {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				rowData[`rul-${cellIndex}`] = cell;
			});

			return rowData;
		});
	}, [failureGradeMatrix, ranges?.fpRanges, currentDeteriorationModel?.id]);

	const GradeMatrixHeader = () => {
		return (
			<>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						px: 3,
						py: 1.5,
						bgcolor: '#ffffff',
						minHeight: '64px',
						boxSizing: 'border-box',
					}}>
					<Box>
						<Typography className={classes.matrixHeaderText}>{t('Failure Grade Matrix')}</Typography>
						<Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.25, ml: '5px' }}>
							{t('Click any cell to reassign its grade.')}
						</Typography>
					</Box>
					<Button
						id="matrix-header-btn-edit-ranges"
						variant="outlined"
						color="primary"
						disabled={isLoading}
						sx={{ flexShrink: 0, mt: 0.5 }}
						onClick={() => setShowEditRangesDialog(true)}>
						{t('Edit Ranges')}
					</Button>
				</Box>
				<Divider />
			</>
		);
	};

	const GradeMatrixFooter = () => {
		return (
			<div id="Grade-grade-legends-list" className={classes.legendsList}>
				{legends.map((legend, key) => (
					<div key={key} className={classes.matrixFooterLegendDiv}>
						<StarIcon
							style={{
								width: '16px',
								marginBottom: '2px',
								color: legend.color,
							}}
						/>
						<Typography className={classes.legendSpans}>{t(legend.displayName.toUpperCase())}</Typography>
					</div>
				))}
			</div>
		);
	};

	return (
		<>
			<RangeEditorDialog
				showEditRangesDialog={showEditRangesDialog}
				setShowEditRangesDialog={setShowEditRangesDialog}
				rulRangeList={ranges?.rulRanges || []}
				fpRangeList={ranges?.fpRanges || []}
				modelId={currentDeteriorationModel?.id || ''}
			/>
			<StyledGradeMatrixDataGrid
				dataCy="Grade-matrix-data-grid"
				disableColumnMenu
				disableColumnReorder
				disableRowSelectionOnClick
				skeletonRowCount={5}
				columns={columns}
				rows={rows}
				slots={{
					toolbar: GradeMatrixHeader,
					footer: GradeMatrixFooter,
				}}
				sx={{
					'& .MuiDataGrid-row:hover': {
						backgroundColor: 'transparent',
						cursor: 'default',
					},
				}}
			/>
		</>
	);
};
